import {useQuery} from '@tanstack/react-query'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

/**
 * Shopper Availability API replaces the Shopper Products `expand=availability` flow.
 * It returns real-time inventory (stockLevel/ats/orderable) for a set of product IDs
 * in a single, lean call and is therefore more performant than re-fetching the full
 * product document just to refresh stock.
 *
 * Docs: https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-availability
 */

/** Max product IDs accepted by a single Shopper Availability call. */
const AVAILABILITY_BATCH_SIZE = 24

const toIdArray = (productIds) => {
    if (Array.isArray(productIds)) return productIds.filter(Boolean)
    if (typeof productIds === 'string' && productIds) return [productIds]
    return []
}

/**
 * Normalize a single Shopper Availability `data[]` entry into the `inventory` shape the
 * storefront already consumes (matching Shopper Products' `inventory` object). Availability
 * is read from the site-assigned default inventory list (the first/only entry).
 * @param {Object} entry - One element of the Shopper Availability `data` array.
 * @returns {Object|undefined} Normalized inventory object, or undefined when absent.
 */
const mapInventory = (entry) => {
    const inventory = entry?.inventories?.[0]
    if (!inventory) return undefined
    return {
        id: inventory.id,
        ats: inventory.ats,
        stockLevel: inventory.stockLevel,
        orderable: inventory.orderable,
        backorderable: inventory.backorderable,
        preorderable: inventory.preorderable
    }
}

/**
 * Build a per-requested-id map from a flat Shopper Availability response.
 *
 * With `expand=variations`, the parent (master/variation group) entry and all of its
 * variant entries are returned flat at the top level. We attribute the entry whose id
 * matches a requested id as the "parent" and treat every other entry as a variant of the
 * single requested parent (only meaningful for single-id, expand=variations calls).
 *
 * @param {Object[]} data - Shopper Availability `data` array.
 * @param {string[]} requestedIds - IDs that were requested (parents).
 * @returns {Object} Map of requestedId -> { inventory, inventoryId, variants }
 */
const buildAvailabilityMap = (data, requestedIds) => {
    const entriesById = new Map((data || []).map((entry) => [entry.id, entry]))
    const requestedSet = new Set(requestedIds)
    const map = {}

    requestedIds.forEach((id) => {
        const parentEntry = entriesById.get(id)
        const inventory = mapInventory(parentEntry)
        map[id] = {
            inventory,
            inventoryId: inventory?.id,
            variants: []
        }
    })

    // Any entry that wasn't itself requested is an expanded variant. When a single parent
    // was requested, all expanded variants belong to it.
    const variantEntries = (data || []).filter((entry) => !requestedSet.has(entry.id))
    if (requestedIds.length === 1 && variantEntries.length) {
        map[requestedIds[0]].variants = variantEntries.map((entry) => {
            const inventory = mapInventory(entry)
            return {
                productId: entry.id,
                orderable: inventory?.orderable,
                inventory
            }
        })
    }

    return map
}

/**
 * Fetch real-time availability for one or more products via the Shopper Availability API.
 *
 * @param {string|string[]} productIds - Product/master/variation-group ID(s).
 * @param {Object} [options]
 * @param {boolean} [options.expandVariations=false] - Include availability for all variants.
 * @param {boolean} [options.enabled=true] - Gate the query (e.g. client-only).
 * @param {number} [options.staleTime=0] - Freshness window in ms. Defaults to 0 so inventory is
 *   refetched on every mount; raise it if slightly staler stock is acceptable.
 * @returns {import('@tanstack/react-query').UseQueryResult<Object>} React Query result whose
 *   `data` is a map of requestedId -> { inventory, inventoryId, variants }.
 */
export const useProductAvailability = (productIds, options = {}) => {
    const {expandVariations = false, enabled = true, staleTime = 0} = options

    const {app: appConfig} = getConfig()
    const {getTokenWhenReady} = useAccessToken()
    const appOrigin = useAppOrigin()
    const {site} = useMultiSite()
    const siteId = site?.id

    const ids = toIdArray(productIds)
    const isClient = typeof window !== 'undefined'

    return useQuery(
        ['product-availability', siteId, expandVariations, ids],
        async () => {
            if (!ids.length || !siteId) return {}

            const token = await getTokenWhenReady()
            const {proxyPath, parameters} = appConfig.commerceAPI
            const {organizationId} = parameters

            // Chunk into batches of 24 (API limit) and fetch in parallel.
            const chunks = []
            for (let i = 0; i < ids.length; i += AVAILABILITY_BATCH_SIZE) {
                chunks.push(ids.slice(i, i + AVAILABILITY_BATCH_SIZE))
            }

            const results = await Promise.all(
                chunks.map(async (chunk) => {
                    const queryParams = [
                        `siteId=${encodeURIComponent(siteId)}`,
                        `productIds=${chunk.map(encodeURIComponent).join(',')}`
                    ]
                    if (expandVariations) queryParams.push('expand=variations')

                    const url = `${appOrigin}${proxyPath}/product/shopper-availability/v1/organizations/${organizationId}/availability?${queryParams.join(
                        '&'
                    )}`

                    const response = await fetch(url, {
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: token ? `Bearer ${token}` : ''
                        }
                    })

                    if (!response.ok) {
                        throw new Error(`Shopper Availability request failed: ${response.status}`)
                    }

                    const json = await response.json()
                    return json?.data || []
                })
            )

            return buildAvailabilityMap(results.flat(), ids)
        },
        {
            enabled: isClient && enabled && ids.length > 0 && Boolean(siteId),
            staleTime,
            keepPreviousData: false,
            refetchOnWindowFocus: false
        }
    )
}

export {buildAvailabilityMap, mapInventory}
